import { useCallback, useEffect, useMemo, useReducer } from 'react'
import type { ReactNode } from 'react'
import type { AuthGateway } from '@domain/ports/auth-gateway'
import { AuthContext } from './auth-context'
import type { AuthContextValue } from './auth-context'
import { INITIAL_SESSION, reduceSession } from './session-state'

type Props = {
  /** Injetada pela composição em `src/main.tsx`. A UI não alcança
   * `adapters/` — a fronteira é lintada e falha o build. */
  gateway: AuthGateway
  children: ReactNode
}

export function AuthProvider({ gateway, children }: Props) {
  const [session, dispatch] = useReducer(reduceSession, INITIAL_SESSION)

  useEffect(() => {
    return gateway.observeUser((user) => {
      dispatch({ type: 'userChanged', user })
    })
  }, [gateway])

  // O bootstrap depende do uid, e não da sessão inteira: assim ele roda uma vez
  // por sessão nova (e uma vez por nova tentativa, que passa por `unprepared` e
  // volta a `preparing`), e não a cada re-render.
  const preparingUid = session.status === 'preparing' ? session.user.uid : null

  useEffect(() => {
    if (preparingUid === null) return

    void gateway
      .bootstrapHousehold()
      .then((result) => {
        dispatch(
          result.ok
            ? { type: 'bootstrapSucceeded', uid: preparingUid }
            : { type: 'bootstrapFailed', uid: preparingUid, refusal: result.refusal },
        )
      })
      .catch((error: unknown) => {
        // O tipo da porta diz que este `catch` não acontece: o adapter devolve
        // recusa, não rejeita. Se acontecer mesmo assim, o app não pode ficar
        // preso em "preparando" para sempre — a falha vira estado visível e
        // fica no console.
        console.error('bootstrapHousehold rejeitou fora do contrato da porta', error)
        dispatch({ type: 'bootstrapFailed', uid: preparingUid, refusal: 'retryable' })
      })
  }, [gateway, preparingUid])

  const signIn = useCallback(() => gateway.signIn(), [gateway])
  const signOut = useCallback(() => gateway.signOut(), [gateway])
  const retryBootstrap = useCallback(() => {
    dispatch({ type: 'bootstrapRetried' })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ session, signIn, signOut, retryBootstrap }),
    [session, signIn, signOut, retryBootstrap],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
